// Third-party
import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

// Components
import { FolderTabs } from '../../../components/navigation';
import { ContactActivityLogs, ContactPortfolios, ContactComparisons, ContactRiskProfiles } from './tabs';

// Actions
import { CurrentContact } from '../../../../redux/modules/types/contacts';

// Utils
import { NAV_PATHS } from '../../../../utils/constants';
import { formatMessage } from '../../../../utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import User from '@/types/user';
import Locale from '@/types/locale';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Comparison from '@/types/comparison';
import Activity from '@/types/activity';
import ReducedComparison from '@/types/reduced-comparison';
import ContactRiskProfile from '@/types/contact-risk-profile';

type Props = RouteComponentProps & {
  user: User;
  locale: Locale;
  currentContact: CurrentContact;
  questionnaires: KycQuestionnaire[];
  onLoadMoreComparisons: () => Promise<Comparison[]>;
  onLoadMoreActivityLogs: () => Promise<Activity[]>;
  onCreateComparison: () => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onViewComparison: (comparison: ReducedComparison) => void;
  onRerunComparison: (comparison: ReducedComparison) => void;
  onSearchComparisons: (query: string) => Promise<ReducedComparison[]>;
  onAttachRiskProfile: (profile: ContactRiskProfile) => void;
  onDeleteRiskProfile: (profile: ContactRiskProfile) => void;
  onEditRiskProfile: (profile: ContactRiskProfile) => void;
  onCreateRiskProfile: (questionnaire?: KycQuestionnaire) => void;
  loading: {
    portfolios: boolean;
    comparisons: boolean;
  };
  attachingRiskProfile: boolean;
  selectedRiskProfileForComparison?: ContactRiskProfile;
  openingComparisonIds: string[];
  rerunningComparisonIds: string[];
};

enum ContactDetailsTab {
  Portfolios = 'Portfolios',
  Comparisons = 'Comparisons',
  RiskProfiles = 'RiskProfiles',
  ActivityLogs = 'ActivityLogs',
}

enum ContactDetailsPaths {
  Portfolios = 'portfolios',
  Comparisons = 'comparisons',
  RiskProfiles = 'risk-profiles',
  ActivityLogs = 'activity',
}

class ContactDetailsTabs extends Component<Props> {
  DEFAULT_TAB = ContactDetailsTab.Portfolios;

  componentDidMount() {
    if (!this.getSelectedTabFromUrl()) {
      const path = this.getContactPathUrl();
      const redirect = `${path}/${ContactDetailsPaths[this.DEFAULT_TAB]}`;
      this.props.history.replace(redirect);
    }
  }

  getSelectedTabFromUrl = () => {
    const { location } = this.props;

    const selectedTab = this.getTabs().find(tab => location.pathname === tab.value);

    return selectedTab ? selectedTab.id : null;
  };

  getCurrentTab = () => this.getSelectedTabFromUrl() || this.DEFAULT_TAB;

  getContactPathUrl = () => {
    const { currentContact } = this.props;

    if (!currentContact || !currentContact.contact._id) {
      return null;
    }

    return NAV_PATHS.CONTACT_DETAILS.replace(':contactId', currentContact.contact._id);
  };

  getTabs = () => {
    const { currentContact, user, questionnaires } = this.props;

    const path = this.getContactPathUrl();

    if (!path) {
      return [];
    }

    return [
      {
        id: ContactDetailsTab.Portfolios,
        value: `${path}/${ContactDetailsPaths.Portfolios}`,
        label: formatMessage('Contacts.ContactTabs.Portfolios.Title'),
      },
      {
        id: ContactDetailsTab.Comparisons,
        value: `${path}/${ContactDetailsPaths.Comparisons}`,
        label: formatMessage('Contacts.ContactTabs.Comparisons.Title'),
      },
      {
        id: ContactDetailsTab.RiskProfiles,
        value: `${path}/${ContactDetailsPaths.RiskProfiles}`,
        label: formatMessage('Contacts.ContactTabs.RiskProfiles.Title'),
        hidden:
          !userHasPermissionEnabled(user, PERMISSIONS.RISK_QUESTIONNAIRE, PERMISSIONS.RISK_PROFILE) ||
          !userHasPermission(user, PERMISSIONS.CONTACTS_RISK_PROFILES_TAB) ||
          questionnaires.length === 0,
      },
      {
        id: ContactDetailsTab.ActivityLogs,
        value: `${path}/${ContactDetailsPaths.ActivityLogs}`,
        label: formatMessage('Contacts.ContactTabs.ActivityLogs.Title'),
        hidden: !userHasPermission(user, PERMISSIONS.ACTIVITY_LOGS) || currentContact.contact.user == null,
      },
    ].filter(tab => !tab.hidden);
  };

  renderTabContent = () => {
    switch (this.getCurrentTab()) {
      case ContactDetailsTab.Portfolios: {
        return this.renderPortfoliosTab();
      }
      case ContactDetailsTab.Comparisons: {
        return this.renderComparisonsTab();
      }
      case ContactDetailsTab.RiskProfiles: {
        return this.renderRiskProfilesTab();
      }
      case ContactDetailsTab.ActivityLogs: {
        return this.renderActivityTab();
      }
    }
  };

  renderPortfoliosTab = () => <ContactPortfolios />;

  renderComparisonsTab = () => {
    const { currentContact, loading, user, openingComparisonIds, rerunningComparisonIds } = this.props;

    return (
      <ContactComparisons
        user={user}
        comparisons={currentContact.comparisons || []}
        contact={currentContact.contact}
        onLoadMore={this.props.onLoadMoreComparisons}
        hasMoreComparisons={currentContact.hasMore.comparisons}
        loading={loading.comparisons}
        onDeleteComparison={this.props.onDeleteComparison}
        onViewComparison={this.props.onViewComparison}
        onRerunComparison={this.props.onRerunComparison}
        onCreateComparison={this.props.onCreateComparison}
        onSearch={this.props.onSearchComparisons}
        openingComparisonIds={openingComparisonIds}
        rerunningComparisonIds={rerunningComparisonIds}
      />
    );
  };

  renderRiskProfilesTab = () => {
    const { currentContact, locale, user, attachingRiskProfile, selectedRiskProfileForComparison } = this.props;

    return (
      <ContactRiskProfiles
        user={user}
        riskProfiles={currentContact.contact.riskProfiles || []}
        contact={currentContact.contact}
        locale={locale}
        loading={attachingRiskProfile}
        selectedRiskProfileForComparison={selectedRiskProfileForComparison}
        onAttachRiskProfile={this.props.onAttachRiskProfile}
        onEditRiskProfile={this.props.onEditRiskProfile}
        onDeleteRiskProfile={this.props.onDeleteRiskProfile}
        onCreateRiskProfile={this.props.onCreateRiskProfile}
      />
    );
  };

  renderActivityTab = () => {
    const { currentContact, loading } = this.props;

    return (
      <ContactActivityLogs
        activityLogs={currentContact.activityLogs || []}
        contact={currentContact.contact}
        onLoadMore={this.props.onLoadMoreActivityLogs}
        hasMoreLogs={!!currentContact.nextActivityLogToken}
        loading={loading.portfolios}
      />
    );
  };

  render() {
    return (
      <div className="contact-details-tabs">
        <FolderTabs selectedId={this.getCurrentTab()} items={this.getTabs()} />

        <div className="contact-tabs-content">{this.renderTabContent()}</div>
      </div>
    );
  }
}

export default withRouter(ContactDetailsTabs);
